import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Layout from '../components/Layout';

const EMPTY_FORM = { ins_item: '', ins_chargeperday: '' };

export default function InsuranceDetail() {
  const navigate = useNavigate();

  // ── Meta ──
  const [daySlabs,   setDaySlabs]   = useState([]); // tbl_insurance_dayslab
  const [allGroups,  setAllGroups]  = useState([]); // tbl_group

  // ── Table ──
  const [data,       setData]       = useState([]);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total,      setTotal]      = useState(0);
  const [search,     setSearch]     = useState('');
  const [loading,    setLoading]    = useState(false);

  // ── Form ──
  const [form,        setForm]       = useState(EMPTY_FORM);
  const [slabPrices,  setSlabPrices] = useState({}); // { slabId: price }
  const [selGroups,   setSelGroups]  = useState([]); // selected group_ids
  const [editId,      setEditId]     = useState(null);
  const [saving,      setSaving]     = useState(false);

  // ── View modal ──
  const [viewItem, setViewItem] = useState(null);

  // ── Auth ──
  useEffect(() => {
    if (!localStorage.getItem('token')) navigate('/login');
  }, []);

  // ── Load day slabs + groups ──
  useEffect(() => {
    API.get('/insurance/day-slabs').then(r => {
      setDaySlabs(r.data);
      // Init slab prices to 0
      const init = {};
      r.data.forEach(s => { init[s.Insuranceslabid] = ''; });
      setSlabPrices(init);
    }).catch(() => toast.error('Failed to load day slabs'));

    API.get('/insurance/groups')
      .then(r => setAllGroups(r.data))
      .catch(() => toast.error('Failed to load groups'));
  }, []);

  // ── Fetch table ──
  const fetchData = useCallback((pg = page, q = search) => {
    setLoading(true);
    API.get('/insurance', { params: { page: pg, limit: 10, search: q } })
      .then(r => {
        setData(r.data.data);
        setTotalPages(r.data.totalPages);
        setTotal(r.data.total);
      })
      .catch(() => toast.error('Failed to load insurance records'))
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { fetchData(page, search); }, [page]);

  useEffect(() => {
    const t = setTimeout(() => { setPage(1); fetchData(1, search); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // ── Default price auto-fill (same as PHP JS) ──
  const handleDefaultPriceChange = (val) => {
    setForm(f => ({ ...f, ins_chargeperday: val }));
    // Auto-fill all slab prices that haven't been manually edited
    setSlabPrices(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(k => {
        if (!updated[k] || updated[k] === form.ins_chargeperday) {
          updated[k] = val;
        }
      });
      return updated;
    });
  };

  // ── Reset form ──
  const resetForm = () => {
    setForm(EMPTY_FORM);
    const init = {};
    daySlabs.forEach(s => { init[s.Insuranceslabid] = ''; });
    setSlabPrices(init);
    setSelGroups([]);
    setEditId(null);
  };

  // ── Edit ──
  const handleEdit = (row) => {
    setEditId(row.ins_id);
    setForm({ ins_item: row.ins_item, ins_chargeperday: row.ins_chargeperday });

    const prices = {};
    daySlabs.forEach(s => {
      prices[s.Insuranceslabid] = row.slab_prices?.[s.Insuranceslabid] ?? '';
    });
    setSlabPrices(prices);
    setSelGroups((row.groups || []).map(g => g.group_id));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Group toggle ──
  const toggleGroup = (gid) => {
    setSelGroups(prev =>
      prev.includes(gid) ? prev.filter(x => x !== gid) : [...prev, gid]
    );
  };

  // ── Submit ──
  const handleSubmit = async () => {
    if (!form.ins_item.trim())       { toast.warning('Item name is required');         return; }
    if (!form.ins_chargeperday)      { toast.warning('Charge per day is required');    return; }
    if (!selGroups.length)           { toast.warning('Select at least one group');     return; }

    setSaving(true);
    try {
      const payload = {
        ins_item:         form.ins_item.trim(),
        ins_chargeperday: parseFloat(form.ins_chargeperday),
        slab_prices:      slabPrices,
        group_ids:        selGroups,
      };

      if (editId) {
        await API.put(`/insurance/${editId}`, payload);
        toast.success('Insurance updated successfully');
      } else {
        await API.post('/insurance', payload);
        toast.success('Insurance added successfully');
      }
      resetForm();
      fetchData(page, search);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Operation failed');
    }
    setSaving(false);
  };

  // ── Delete ──
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this insurance record?')) return;
    try {
      await API.delete(`/insurance/${id}`);
      toast.success('Deleted successfully');
      fetchData(page, search);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  const slabLabel = (s) => {
    const to = s.ToIns_slab === 1000 ? 'More' : s.ToIns_slab;
    return `${s.FromIns_slab}–${to}`;
  };

  return (
    <Layout activePage="Insurance Details">
      <ToastContainer position="top-right" autoClose={3000} />

      <div style={S.header}>
        <h2 style={S.title}>Manage Insurance Details</h2>
        <p style={S.subtitle}>Add and manage Insurance Details</p>
      </div>

      {/* ── Add / Edit Form Card ── */}
      <div style={S.formCard}>
        <div style={S.formCardHead}>
          <div>
            <h2 style={S.formTitle}>
              {editId ? '✏️ Edit Insurance Details' : '➕ Add Insurance Details'}
            </h2>
            {editId && <span style={S.editBadge}>Editing #{editId}</span>}
          </div>
          {editId && (
            <button style={S.cancelBtn} onClick={resetForm}>✕ Cancel Edit</button>
          )}
        </div>

        {/* Row 1: Item name + default price */}
        <div style={S.grid2} >
          <div style={S.fg}>
            <label style={S.label}>Item Name <span style={S.req}>*</span></label>
            <input
              value={form.ins_item}
              onChange={e => setForm(f => ({ ...f, ins_item: e.target.value }))}
              placeholder="e.g. Personal Accident Insurance (PAI)"
              style={S.input}
            />
          </div>
          <div style={S.fg}>
            <label style={S.label}>Default Price <span style={S.req}>*</span></label>
            <input
              type="number" min="0" step="0.01"
              value={form.ins_chargeperday}
              onChange={e => handleDefaultPriceChange(e.target.value)}
              placeholder="e.g. 10.00"
              style={S.input}
            />
            <span style={{ fontSize: 11, color: '#aaa' }}>
              Typing here auto-fills all slab prices
            </span>
          </div>
        </div>

        {/* Row 2: Insurance day slab prices */}
        {daySlabs.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={S.sectionLabel}>Insurance Day Slab Prices</div>
            <div style={S.slabGrid}>
              {daySlabs.map(s => (
                <div key={s.Insuranceslabid} style={S.fg}>
                  <label style={{ ...S.label, textAlign: 'center' }}>{slabLabel(s)}</label>
                  <input
                    type="number" min="0" step="0.01"
                    value={slabPrices[s.Insuranceslabid] ?? ''}
                    onChange={e => setSlabPrices(p => ({ ...p, [s.Insuranceslabid]: e.target.value }))}
                    style={{ ...S.input, textAlign: 'center' }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Row 3: Group checkboxes */}
        <div style={{ marginTop: 16 }}>
          <div style={S.sectionLabel}>
            Car Groups <span style={S.req}>*</span>
          </div>
          <div style={S.groupGrid}>
            {allGroups.map(g => {
              const checked = selGroups.includes(g.group_id);
              return (
                <label key={g.group_id} style={{
                  ...S.checkLabel,
                  background: checked ? '#f3f2ff' : '#fafafa',
                  border: `1.5px solid ${checked ? '#6C63FF' : '#e0e0e0'}`,
                  color: checked ? '#6C63FF' : '#555',
                }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleGroup(g.group_id)}
                    style={{ accentColor: '#6C63FF' }}
                  />
                  {g.group_name}
                </label>
              );
            })}
          </div>
        </div>

        {/* Submit */}
        <div style={{ marginTop: 20, display: 'flex', gap: 10 }}>
          {editId && (
            <button style={S.cancelBtn} onClick={resetForm}>Clear</button>
          )}
          <button style={S.submitBtn} onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving…' : editId ? 'Update' : 'Add Insurance'}
          </button>
        </div>
      </div>

      {/* ── Manage Table Card ── */}
      <div style={{ ...S.card, marginTop: 24 }}>

        {/* Table header + search */}
        <div style={S.tableHead}>
          <span style={S.tableTitle}>Manage Insurance Details</span>
          <div style={S.searchWrap}>
            <span style={{ fontSize: 13, color: '#aaa' }}>🔍</span>
            <input type="text" placeholder="Search item name..."
              value={search} onChange={e => setSearch(e.target.value)}
              style={S.searchInput} />
            {search && <button style={S.clearBtn} onClick={() => setSearch('')}>✕</button>}
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr style={S.theadRow}>
                <th style={S.th}>#</th>
                <th style={S.th}>Item Name</th>
                <th style={S.th}>Charge/Day</th>
                {/* Dynamic slab columns */}
                {daySlabs.map(s => (
                  <th key={s.Insuranceslabid} style={{ ...S.th, textAlign: 'center' }}>
                    {slabLabel(s)}
                  </th>
                ))}
                <th style={S.th}>Groups</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5 + daySlabs.length} style={S.emptyCell}>Loading…</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={5 + daySlabs.length} style={S.emptyCell}>No records available</td></tr>
              ) : data.map((row, i) => (
                <tr key={row.ins_id} style={{
                  ...S.tr,
                  background: editId === row.ins_id ? '#f3f2ff' : 'transparent'
                }}>
                  <td style={S.td}>
                    <span style={S.serial}>{(page - 1) * 10 + i + 1}</span>
                  </td>
                  <td style={S.td}>
                    <span style={S.nameChip}>{row.ins_item}</span>
                  </td>
                  <td style={S.td}>
                    <span style={S.priceChip}>{row.ins_chargeperday}</span>
                  </td>
                  {/* Slab prices */}
                  {daySlabs.map(s => (
                    <td key={s.Insuranceslabid} style={{ ...S.td, textAlign: 'center' }}>
                      <span style={S.slabVal}>
                        {row.slab_prices?.[s.Insuranceslabid] ?? '—'}
                      </span>
                    </td>
                  ))}
                  {/* Groups */}
                  <td style={S.td}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {(row.groups || []).map(g => (
                        <span key={g.group_id} style={S.groupTag}>{g.group_name}</span>
                      ))}
                      {!row.groups?.length && <span style={{ color: '#ccc', fontSize: 12 }}>—</span>}
                    </div>
                  </td>
                  {/* Actions */}
                  <td style={{ ...S.td, textAlign: 'right' }}>
                    <button style={S.viewBtn} onClick={() => setViewItem(row)}>View</button>
                    <button style={S.editBtn} onClick={() => handleEdit(row)}>Edit</button>
                    <button style={S.delBtn}  onClick={() => handleDelete(row.ins_id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={S.pagination}>
          <span style={S.paginInfo}>
            {total > 0 ? `Showing ${(page-1)*10+1}–${Math.min(page*10, total)} of ${total}` : ''}
          </span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button style={S.pgBtn} disabled={page === 1} onClick={() => setPage(p => p-1)}>← Prev</button>
            <span style={S.paginInfo}>Page {page} / {totalPages || 1}</span>
            <button style={S.pgBtn} disabled={page === totalPages || totalPages === 0}
              onClick={() => setPage(p => p+1)}>Next →</button>
          </div>
        </div>
      </div>

      {/* ── View Modal ── */}
      {viewItem && (
        <div style={S.overlay} onClick={() => setViewItem(null)}>
          <div style={S.modalBox} onClick={e => e.stopPropagation()}>
            <div style={S.modalHead}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                🛡️ {viewItem.ins_item}
              </h3>
              <button style={S.closeBtn} onClick={() => setViewItem(null)}>✕</button>
            </div>
            <div style={S.modalBody}>

              {/* Basic */}
              <div style={S.viewRow}>
                <span style={S.viewKey}>Default Charge/Day</span>
                <span style={S.priceChip}>{viewItem.ins_chargeperday}</span>
              </div>

              {/* Slab prices */}
              {daySlabs.length > 0 && (
                <>
                  <div style={{ ...S.sectionLabel, marginTop: 16 }}>Day Slab Prices</div>
                  <div style={S.slabGrid}>
                    {daySlabs.map(s => (
                      <div key={s.Insuranceslabid} style={S.viewSlabBox}>
                        <div style={{ fontSize: 11, color: '#aaa', fontWeight: 700, textAlign: 'center' }}>
                          {slabLabel(s)}
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 800, color: '#6C63FF', textAlign: 'center', marginTop: 4 }}>
                          {viewItem.slab_prices?.[s.Insuranceslabid] ?? '—'}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Groups */}
              <div style={{ ...S.sectionLabel, marginTop: 16 }}>Assigned Groups</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                {(viewItem.groups || []).map(g => (
                  <span key={g.group_id} style={{ ...S.groupTag, fontSize: 13, padding: '5px 14px' }}>
                    {g.group_name}
                  </span>
                ))}
                {!viewItem.groups?.length && <span style={{ color: '#bbb', fontSize: 13 }}>No groups assigned</span>}
              </div>
            </div>
            <div style={S.modalFoot}>
              <button style={S.submitBtn} onClick={() => setViewItem(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

/* ─── Styles ─── */
const S = {
  formCard:     { background: '#fff', borderRadius: 16, padding: '24px',
    boxShadow: '0 2px 16px rgba(0,0,0,0.06)', marginBottom: 0 },
  formCardHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: 20, flexWrap: 'wrap', gap: 10 },
  formTitle:    { margin: 0, fontSize: 20, fontWeight: 800, color: '#1a1d2e' },
  editBadge:    { fontSize: 11, fontWeight: 700, background: '#f3f2ff', color: '#6C63FF',
    borderRadius: 20, padding: '3px 10px', display: 'inline-block', marginTop: 4 },
 /* Header */
 header: { marginBottom: 20 },
 title: { margin: 0, fontSize: 26, fontWeight: 800, color: '#1a1d2e', letterSpacing: '-0.5px' },
 subtitle: { margin: '4px 0 0', fontSize: 13, color: '#999' },


  sectionLabel: { fontSize: 11, fontWeight: 800, color: '#6C63FF',
    textTransform: 'uppercase', letterSpacing: '0.7px',
    borderBottom: '1.5px solid #f3f2ff', paddingBottom: 6, marginBottom: 12 },

  slabGrid:  { display: 'flex', gap: 10, flexWrap: 'wrap' },
  groupGrid: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 },

  checkLabel: { display: 'flex', alignItems: 'center', gap: 6,
    padding: '6px 14px', borderRadius: 20, cursor: 'pointer',
    fontSize: 13, fontWeight: 600, transition: 'all 0.15s', userSelect: 'none' },

  card:      { background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,0.06)', overflow: 'hidden' },
  tableHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 20px', borderBottom: '1px solid #f0f0f0', flexWrap: 'wrap', gap: 12 },
  tableTitle:{ fontSize: 14, fontWeight: 700, color: '#1a1d2e' },
  searchWrap:{ display: 'flex', alignItems: 'center', gap: 8, background: '#f8f8f8',
    border: '1px solid #e8e8e8', borderRadius: 10, padding: '7px 14px', minWidth: 220 },
  searchInput:{ border: 'none', outline: 'none', fontSize: 13, background: 'transparent',
    fontFamily: 'inherit', color: '#333', flex: 1 },
  clearBtn:  { background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: 12, padding: 0 },

  table:     { width: '100%', borderCollapse: 'collapse' },
  theadRow:  { background: '#fafafa' },
  th:        { padding: '11px 14px', fontSize: 11, fontWeight: 700, color: '#999',
    textAlign: 'left', borderBottom: '2px solid #f0f0f0',
    textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
  tr:        { transition: 'background 0.12s' },
  td:        { padding: '12px 14px', fontSize: 13, color: '#333', borderBottom: '1px solid #f8f8f8' },
  emptyCell: { textAlign: 'center', padding: 40, color: '#bbb', fontSize: 14 },

  serial:    { background: '#f3f2ff', color: '#6C63FF', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 700 },
  nameChip:  { background: '#f0fff6', color: '#1a9e5c', borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 600 },
  priceChip: { background: '#f3f2ff', color: '#6C63FF', borderRadius: 20, padding: '4px 12px', fontSize: 13, fontWeight: 700 },
  slabVal:   { background: '#f8f8f8', color: '#555', borderRadius: 8, padding: '3px 8px', fontSize: 12, fontWeight: 600 },
  groupTag:  { background: '#f0f8ff', color: '#1a6eb5', border: '1px solid #cce5ff',
    borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600 },

  viewBtn:   { marginRight: 5, background: '#f0fff6', color: '#1a9e5c',
    border: '1px solid #b8f0d0', padding: '4px 12px',
    borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit' },
  editBtn:   { marginRight: 5, background: '#f3f2ff', color: '#6C63FF',
    border: '1px solid #d4d0ff', padding: '4px 12px',
    borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit' },
  delBtn:    { background: '#fff2f5', color: '#FF6584', border: '1px solid #ffd0d8',
    padding: '4px 12px', borderRadius: 20, cursor: 'pointer',
    fontSize: 12, fontWeight: 700, fontFamily: 'inherit' },

  pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 20px', borderTop: '1px solid #f0f0f0' },
  paginInfo:  { fontSize: 12, color: '#999' },
  pgBtn:      { padding: '5px 14px', background: '#f3f2ff', color: '#6C63FF',
    border: '1px solid #d4d0ff', borderRadius: 8, cursor: 'pointer',
    fontSize: 12, fontWeight: 600, fontFamily: 'inherit' },

  overlay:    { position: 'fixed', inset: 0, background: 'rgba(10,10,30,0.55)', zIndex: 999,
    display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)' },
  modalBox:   { background: '#fff', borderRadius: 20, width: 580, maxWidth: '96vw', maxHeight: '88vh',
    display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.2)' },
  modalHead:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '18px 22px', borderBottom: '1px solid #f0f0f0', flexShrink: 0 },
  modalBody:  { padding: '20px 22px', overflowY: 'auto', flex: 1 },
  modalFoot:  { display: 'flex', justifyContent: 'flex-end', gap: 10,
    padding: '14px 22px', borderTop: '1px solid #f0f0f0', flexShrink: 0 },
  closeBtn:   { background: '#ff6b6b22', border: 'none', color: '#ff6b6b',
    borderRadius: '50%', width: 30, height: 30, cursor: 'pointer', fontWeight: 800, fontSize: 14 },

  viewRow:    { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 },
  viewKey:    { fontSize: 13, fontWeight: 600, color: '#888', minWidth: 140 },
  viewSlabBox:{ background: '#f8f8f8', borderRadius: 10, padding: '10px 16px', minWidth: 80 },

  cancelBtn:  { padding: '9px 18px', background: '#f5f5f5', color: '#666',
    border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  submitBtn:  { padding: '10px 24px', background: 'linear-gradient(135deg,#6C63FF,#43BCCD)',
    color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer',
    fontSize: 13, fontWeight: 700, fontFamily: 'inherit' },

  grid2:  { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  fg:     { display: 'flex', flexDirection: 'column', gap: 5 },
  label:  { fontSize: 12, fontWeight: 600, color: '#555' },
  req:    { color: '#ff6b6b' },
  input:  { padding: '9px 14px', border: '1.5px solid #e0e0e0', borderRadius: 10,
    fontSize: 13, fontFamily: 'inherit', color: '#333', outline: 'none',
    width: '100%', boxSizing: 'border-box' },
};
